import FormFieldController from "confinus_ui/forms/form_field_controller";
import Sortable from "sortablejs";
import Cropper from "cropperjs";

export default class MultiImageUploadController extends FormFieldController {
  public static targets = [
    ...FormFieldController.targets,
    "sortableContainer",
    "thumbnail",
    "thumbnailImage",
    "fileInput",
    "hiddenFieldsContainer",
    "newFilesInput",
    "imageCount",
    "primaryBadge",
    "cropModal",
    "cropImage",
  ];

  public static values = {
    ...FormFieldController.values,
    maxSizeInMb: Number,
    maxImages: Number,
    enableCrop: Boolean,
    cropAspectW: Number,
    cropAspectH: Number,
    fileMimeTypes: String,
  };

  declare private readonly maxSizeInMbValue: number;
  declare private readonly maxImagesValue: number;
  declare private readonly enableCropValue: boolean;
  declare private readonly cropAspectWValue: number;
  declare private readonly cropAspectHValue: number;
  declare private readonly fileMimeTypesValue: string;

  declare private readonly sortableContainerTarget: HTMLDivElement;
  declare private readonly fileInputTarget: HTMLInputElement;
  declare private readonly hiddenFieldsContainerTarget: HTMLDivElement;
  declare private readonly newFilesInputTarget: HTMLInputElement;
  declare private readonly imageCountTarget: HTMLElement;
  declare private readonly cropModalTarget: HTMLDivElement;
  declare private readonly cropImageTarget: HTMLImageElement;
  declare private readonly hasCropModalTarget: boolean;
  declare private readonly hasCropImageTarget: boolean;

  private sortable?: Sortable;
  private pendingFiles: Map<string, File> = new Map();
  private removedSignedIds: Set<string> = new Set();
  private cropper?: Cropper;
  private croppingThumbnail?: HTMLElement;
  private nextClientId = 0;

  public connect() {
    super.connect();
    this.initSortable();
  }

  public disconnect() {
    if (this.sortable) {
      this.sortable.destroy();
    }
    this.destroyCropper();
    super.disconnect();
  }

  public openFilePicker() {
    if (this.totalImageCount >= this.maxImagesValue) {
      alert(`Maximum of ${this.maxImagesValue} images allowed.`);
      return;
    }
    this.fileInputTarget.click();
  }

  public filesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const remaining = this.maxImagesValue - this.totalImageCount;
    if (remaining <= 0) {
      alert(`Maximum of ${this.maxImagesValue} images allowed.`);
      input.value = "";
      return;
    }

    const files = Array.from(input.files).slice(0, remaining);
    const maxBytes = this.maxSizeInMbValue * 1024 * 1024;

    for (const file of files) {
      if (file.size > maxBytes) {
        alert(`"${file.name}" is too large (max ${this.maxSizeInMbValue}MB).`);
        continue;
      }
      this.addPendingFile(file);
    }

    // Clear the file input so the same file can be re-selected
    input.value = "";
    this.syncHiddenFields();
    this.updateImageCount();
    this.updatePrimaryBadge();
  }

  public removeImage(event: Event) {
    const button = (event.target as HTMLElement).closest("button");
    const thumbnail = button?.closest(
      "[data-image-type]",
    ) as HTMLElement | null;
    if (!thumbnail) return;
    this.removeThumbnail(thumbnail);
  }

  public cropImage(event: Event) {
    if (!this.enableCropValue || !this.hasCropModalTarget) return;

    const button = (event.target as HTMLElement).closest("button");
    const thumbnail = button?.closest(
      "[data-image-type]",
    ) as HTMLElement | null;
    if (!thumbnail) return;

    const img = thumbnail.querySelector("img") as HTMLImageElement | null;
    if (!img) return;

    this.croppingThumbnail = thumbnail;
    this.cropImageTarget.src = img.src;
    this.cropModalTarget.classList.remove("hidden");

    // Wait for image to load before initializing cropper
    this.cropImageTarget.onload = () => {
      this.destroyCropper();

      const aspectRatio =
        this.cropAspectWValue > 0 && this.cropAspectHValue > 0
          ? this.cropAspectWValue / this.cropAspectHValue
          : NaN;

      this.cropper = new Cropper(this.cropImageTarget, {
        aspectRatio,
      });
    };
  }

  public applyCrop() {
    if (!this.cropper || !this.croppingThumbnail) return;

    const canvas = this.cropper.getCroppedCanvas();
    if (!canvas) {
      this.cancelCrop();
      return;
    }

    const thumbnail = this.croppingThumbnail;
    const imageType = thumbnail.dataset.imageType;
    const clientId = thumbnail.dataset.clientId;

    // Use data URL for preview (blob: URLs blocked by CSP)
    const dataUrl = canvas.toDataURL("image/png");
    const img = thumbnail.querySelector("img") as HTMLImageElement;
    if (img) {
      img.src = dataUrl;
    }

    canvas.toBlob((blob: Blob | null) => {
      if (!blob) {
        this.cancelCrop();
        return;
      }

      if (imageType === "existing") {
        // For existing images: mark old one for removal, add cropped as new pending
        const signedId = thumbnail.dataset.signedId;
        if (signedId) {
          this.removedSignedIds.add(signedId);
        }
        // Convert to a "pending" thumbnail
        const newClientId = this.generateClientId();
        const file = new File([blob], `cropped-${Date.now()}.png`, {
          type: "image/png",
        });
        this.pendingFiles.set(newClientId, file);
        thumbnail.dataset.imageType = "pending";
        thumbnail.dataset.clientId = newClientId;
        delete thumbnail.dataset.signedId;
        delete thumbnail.dataset.blobId;
      } else if (imageType === "pending" && clientId) {
        // Replace the pending file with the cropped version
        const file = new File([blob], `cropped-${Date.now()}.png`, {
          type: "image/png",
        });
        this.pendingFiles.set(clientId, file);
      }

      this.syncHiddenFields();
      this.cancelCrop();
    }, "image/png");
  }

  public cancelCrop() {
    this.destroyCropper();
    if (this.hasCropModalTarget) {
      this.cropModalTarget.classList.add("hidden");
    }
    this.croppingThumbnail = undefined;
  }

  // --- Private ---

  private initSortable() {
    this.sortable = Sortable.create(this.sortableContainerTarget, {
      animation: 150,
      ghostClass: "opacity-50",
      dragClass: "cursor-grabbing",
      onEnd: () => {
        this.syncHiddenFields();
        this.updatePrimaryBadge();
      },
    });
  }

  private addPendingFile(file: File) {
    const clientId = this.generateClientId();
    this.pendingFiles.set(clientId, file);

    // Create thumbnail element
    const thumb = document.createElement("div");
    thumb.className =
      "relative group border rounded-lg overflow-hidden bg-gray-50 aspect-square";
    thumb.setAttribute(`data-${this.identifier}-target`, "thumbnail");
    thumb.dataset.imageType = "pending";
    thumb.dataset.clientId = clientId;

    const img = document.createElement("img");
    img.className = "w-full h-full object-cover";
    img.alt = file.name;
    thumb.appendChild(img);

    // Read file for preview
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        img.src = e.target.result.toString();
      }
    };
    reader.readAsDataURL(file);

    // Overlay
    const overlay = document.createElement("div");
    overlay.className =
      "absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors";
    thumb.appendChild(overlay);

    // Action buttons
    const actions = document.createElement("div");
    actions.className =
      "absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity";

    if (this.enableCropValue) {
      const cropBtn = document.createElement("button");
      cropBtn.type = "button";
      cropBtn.className =
        "p-1 bg-white rounded shadow hover:bg-gray-100 text-gray-700";
      cropBtn.title = "Crop image";
      cropBtn.dataset.action = `${this.identifier}#cropImage`;
      cropBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 7V3m0 4H3m4 0l10 10m0 0v4m0-4h4M7 7l10 10" /></svg>`;
      actions.appendChild(cropBtn);
    }

    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.className =
      "p-1 bg-white rounded shadow hover:bg-red-100 text-red-600";
    removeBtn.title = "Remove image";
    removeBtn.dataset.action = `${this.identifier}#removeImage`;
    removeBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>`;
    actions.appendChild(removeBtn);

    thumb.appendChild(actions);
    this.sortableContainerTarget.appendChild(thumb);
  }

  private removeThumbnail(thumbnail: HTMLElement) {
    const imageType = thumbnail.dataset.imageType;

    if (imageType === "existing") {
      const signedId = thumbnail.dataset.signedId;
      if (signedId) {
        this.removedSignedIds.add(signedId);
      }
    } else if (imageType === "pending") {
      const clientId = thumbnail.dataset.clientId;
      if (clientId) {
        this.pendingFiles.delete(clientId);
      }
    }

    thumbnail.remove();
    this.syncHiddenFields();
    this.updateImageCount();
    this.updatePrimaryBadge();
  }

  private syncHiddenFields() {
    const container = this.hiddenFieldsContainerTarget;
    container.innerHTML = "";

    const objectName = this.newFilesInputTarget.name.replace(
      /\[new_images\]\[\]$/,
      "",
    );

    // Add hidden inputs for removed signed IDs
    this.removedSignedIds.forEach((signedId) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = `${objectName}[remove_image_ids][]`;
      input.value = signedId;
      container.appendChild(input);
    });

    // Build image_order from current DOM order and sync new_files input
    const thumbnails =
      this.sortableContainerTarget.querySelectorAll("[data-image-type]");
    const orderedPendingFiles: File[] = [];

    thumbnails.forEach((thumb) => {
      const el = thumb as HTMLElement;
      const imageType = el.dataset.imageType;

      if (imageType === "existing") {
        const signedId = el.dataset.signedId;
        if (signedId) {
          const orderInput = document.createElement("input");
          orderInput.type = "hidden";
          orderInput.name = `${objectName}[image_order][]`;
          orderInput.value = signedId;
          container.appendChild(orderInput);
        }
      } else if (imageType === "pending") {
        const clientId = el.dataset.clientId;
        if (clientId) {
          const file = this.pendingFiles.get(clientId);
          if (file) {
            orderedPendingFiles.push(file);
          }
          // Use a placeholder for ordering — new images get "new_N" markers
          const orderInput = document.createElement("input");
          orderInput.type = "hidden";
          orderInput.name = `${objectName}[image_order][]`;
          orderInput.value = `new_${clientId}`;
          container.appendChild(orderInput);
        }
      }
    });

    // Use DataTransfer API to set files on the new_files input
    this.setFilesOnInput(this.newFilesInputTarget, orderedPendingFiles);
  }

  private setFilesOnInput(input: HTMLInputElement, files: File[]) {
    const dt = new DataTransfer();
    files.forEach((f) => dt.items.add(f));
    input.files = dt.files;
  }

  private updateImageCount() {
    const count = this.totalImageCount;
    this.imageCountTarget.textContent = `${count} / ${this.maxImagesValue} images`;
  }

  private updatePrimaryBadge() {
    // Remove all existing primary badges (both server-rendered and JS-created)
    this.sortableContainerTarget
      .querySelectorAll("[data-primary-badge]")
      .forEach((badge) => badge.remove());
    this.sortableContainerTarget
      .querySelectorAll(`[data-${this.identifier}-target="primaryBadge"]`)
      .forEach((badge) => badge.remove());

    // Add badge to first thumbnail
    const first = this.sortableContainerTarget.querySelector(
      "[data-image-type]",
    ) as HTMLElement | null;
    if (first) {
      const badge = document.createElement("span");
      badge.className =
        "absolute top-1 left-1 bg-primary-600 text-white text-xs px-1.5 py-0.5 rounded font-medium";
      badge.textContent = "Primary";
      badge.dataset.primaryBadge = "true";
      first.appendChild(badge);
    }
  }

  private get totalImageCount(): number {
    return this.sortableContainerTarget.querySelectorAll("[data-image-type]")
      .length;
  }

  private generateClientId(): string {
    return `client_${this.nextClientId++}_${Date.now()}`;
  }

  private destroyCropper() {
    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = undefined;
    }
  }
}
